'use client';

import { useState, useEffect } from 'react';
import { getErrorMessage } from '@/lib/api-utils';

interface UseBulkEmailMessages {
  validationError: string;
  sent: string; // must contain {sentCount} placeholder
  sendFailed: string;
  forbidden: string;
}

interface UseBulkEmailOptions {
  groupId?: number;
  eventId?: number;
  mode?: 'group' | 'bulk' | 'event';
  approvedMembers: Array<{ user: { id: number } }>;
  messages: UseBulkEmailMessages;
  onToast: (message: string, type: 'success' | 'error') => void;
}

export function useBulkEmail({ groupId, eventId, mode = 'group', approvedMembers, messages, onToast }: UseBulkEmailOptions) {
  const [selectedMemberIds, setSelectedMemberIds] = useState<number[]>([]);
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const [bulkEmailSubject, setBulkEmailSubject] = useState('');
  const [bulkEmailMessage, setBulkEmailMessage] = useState('');
  const [sendingBulkEmail, setSendingBulkEmail] = useState(false);

  useEffect(() => {
    const approvedMemberIds = new Set(approvedMembers.map((m) => m.user.id));
    setSelectedMemberIds((prev) => {
      const next = prev.filter((id) => approvedMemberIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [approvedMembers]);

  const closeBulkEmailModal = () => {
    if (sendingBulkEmail) return;
    setShowBulkEmailModal(false);
    setBulkEmailSubject('');
    setBulkEmailMessage('');
  };

  const toggleMemberSelection = (memberId: number) => {
    setSelectedMemberIds((prev) =>
      prev.includes(memberId) ? prev.filter((id) => id !== memberId) : [...prev, memberId]
    );
  };

  const toggleSelectAllMembers = () => {
    if (selectedMemberIds.length === approvedMembers.length) {
      setSelectedMemberIds([]);
      return;
    }
    setSelectedMemberIds(approvedMembers.map((m) => m.user.id));
  };

  const handleSendBulkEmail = async () => {
    if (selectedMemberIds.length === 0 || !bulkEmailSubject.trim() || !bulkEmailMessage.trim()) {
      onToast(messages.validationError, 'error');
      return;
    }

    setSendingBulkEmail(true);

    try {
      let res: Response;
      if (mode === 'event') {
        res = await fetch('/api/strapi/email/send-event', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            eventId,
            userIds: selectedMemberIds,
            subject: bulkEmailSubject.trim(),
            message: bulkEmailMessage.trim(),
          }),
        });
      } else if (mode === 'bulk') {
        res = await fetch('/api/strapi/email/send-bulk', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userIds: selectedMemberIds,
            subject: bulkEmailSubject.trim(),
            message: bulkEmailMessage.trim(),
          }),
        });
      } else {
        res = await fetch('/api/strapi/email/send-group', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            groupId,
            memberIds: selectedMemberIds,
            subject: bulkEmailSubject.trim(),
            message: bulkEmailMessage.trim(),
          }),
        });
      }

      if (res.ok) {
        const data = await res.json() as { sentCount?: number };
        if ((data.sentCount ?? 0) > 0) {
          onToast(messages.sent.replace('{sentCount}', String(data.sentCount ?? 0)), 'success');
          setSelectedMemberIds([]);
          closeBulkEmailModal();
        } else {
          onToast(messages.sendFailed, 'error');
        }
        return;
      }

      if (res.status === 400) {
        onToast((await getErrorMessage(res)) || messages.validationError, 'error');
      } else if (res.status === 403) {
        onToast((await getErrorMessage(res)) || messages.forbidden, 'error');
      } else {
        onToast(messages.sendFailed, 'error');
      }
    } catch (error) {
      console.error('Failed to send group bulk email:', error);
      onToast(messages.sendFailed, 'error');
    } finally {
      setSendingBulkEmail(false);
    }
  };

  return {
    selectedMemberIds,
    showBulkEmailModal,
    setShowBulkEmailModal,
    bulkEmailSubject,
    setBulkEmailSubject,
    bulkEmailMessage,
    setBulkEmailMessage,
    sendingBulkEmail,
    toggleMemberSelection,
    toggleSelectAllMembers,
    handleSendBulkEmail,
    closeBulkEmailModal,
  };
}
